import { FC, useEffect, useState } from 'react'
import { Select } from 'antd'
import { CheckBoxLabel } from '@UI/CheckBox'
import { InputNew } from '@UI/InputNew'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { usersAPI } from '@/api'
import spclasses from '../../../../../SetCourseClassDirectionModal/purchase.module.css'

type TeacherOption = {
  value: number
  label: string
  rate: number | null
}

type AdditionalCourseTeacherSelectProps = {
  courseId?: number | null
  teacherId?: number | null
  salaryPerHour?: number | null
  onChangeTeacher: (
    teacherId: number | null,
    salaryPerHour: number | null,
  ) => void
}

const NO_TEACHERS_TOOLTIP =
  'В выбранном курсе пока нет педагогов со ставкой. Добавьте педагога в настройках курса.'

/** Педагог для доп. hour/minute (controlled, без Form). */
const AdditionalCourseTeacherSelect: FC<AdditionalCourseTeacherSelectProps> = ({
  courseId,
  teacherId,
  salaryPerHour,
  onChangeTeacher,
}) => {
  const [isAttachTeacher, setIsAttachTeacher] = useState(teacherId != null)
  const [options, setOptions] = useState<TeacherOption[]>([])
  const [loading, setLoading] = useState(false)

  const hasTeachers = options.length > 0
  const isCheckboxDisabled = !loading && !hasTeachers

  useEffect(() => {
    setIsAttachTeacher(teacherId != null)
  }, [teacherId])

  useEffect(() => {
    if (!courseId) {
      setOptions([])
      return
    }

    let cancelled = false
    setLoading(true)
    usersAPI
      .getSeminarian({
        direction_id: courseId,
        expand: ['seminarian'],
        pageSize: false,
      })
      .then((res) => {
        if (cancelled) return
        const rows = (res?.data?.data ?? []) as Array<{
          seminarian_id?: number | null
          rate?: number | string | null
          seminarian?: { id?: number; name?: string | null } | null
        }>

        setOptions(
          rows
            .map((row) => {
              const id = Number(row.seminarian_id ?? row.seminarian?.id ?? NaN)
              if (!Number.isFinite(id)) return null
              const rateRaw = Number(row.rate)
              return {
                value: id,
                label: row.seminarian?.name || `Педагог #${id}`,
                rate: Number.isFinite(rateRaw) ? rateRaw : null,
              }
            })
            .filter(Boolean) as TeacherOption[],
        )
      })
      .catch(() => {
        if (!cancelled) setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [courseId])

  const checkbox = (
    <CheckBoxLabel
      type="button"
      value={isAttachTeacher}
      disabled={isCheckboxDisabled}
      onClick={() => {
        if (isCheckboxDisabled) return
        const next = !isAttachTeacher
        setIsAttachTeacher(next)
        if (!next) onChangeTeacher(null, null)
      }}
      label="Привязать педагога"
      className={spclasses.checkboxWithLabel}
      style={{
        justifyContent: 'flex-start',
        paddingTop: '4px',
        paddingBottom: '4px',
        height: 'auto',
      }}
    />
  )

  return (
    <div className={spclasses.inputWithLabel}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isCheckboxDisabled ? (
          <TooltipWrapper placement="top" title={NO_TEACHERS_TOOLTIP}>
            <span style={{ display: 'inline-flex' }}>{checkbox}</span>
          </TooltipWrapper>
        ) : (
          checkbox
        )}
      </div>

      {isAttachTeacher && !isCheckboxDisabled ? (
        <div style={{ paddingTop: '4px' }}>
          <div className={spclasses.inputLabel}>Педагог</div>
          <Select
            showSearch
            loading={loading}
            className={spclasses.select}
            placeholder="Выберите педагога"
            optionFilterProp="label"
            value={teacherId ?? undefined}
            options={options}
            onChange={(next) => {
              const id = next == null ? null : Number(next)
              const selected = options.find((item) => item.value === id)
              onChangeTeacher(id, selected?.rate ?? null)
            }}
          />
          {salaryPerHour != null ? (
            <div className={spclasses.tariffTypeHint} style={{ marginTop: 4 }}>
              Ставка: {salaryPerHour} ₽/час
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default AdditionalCourseTeacherSelect
